import { ChevronDown, ChevronUp, ExternalLink, Paperclip } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { ADMIN_BASE_URL, adminHref } from '@/config/config'
import { formatDateTime } from '@/lib/formatters'
import { EmailDirection, type EmailThreadMessage as Message } from '@/models/internalEmail'
import { QaOutcomeBadge } from '../../QA/Emails/QaOutcomeBadge'
import { EmailMessageBody } from './EmailMessageBody'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function Attachments({ message }: { message: Message }) {
  if (message.attachments.length === 0) return null
  return (
    <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
      {message.attachments.map((attachment) => {
        const label = (
          <>
            <Paperclip className="text-muted-foreground size-3.5 shrink-0" />
            <span className="min-w-0 truncate font-medium sm:max-w-[16rem]">{attachment.fileName}</span>
            <span className="text-muted-foreground text-[10px]">{formatSize(attachment.fileSize)}</span>
          </>
        )
        const className =
          'bg-muted/40 hover:bg-muted flex min-w-0 max-w-full items-center gap-2 rounded-md border px-2 py-1.5 text-xs transition-colors'
        return attachment.fileUrl ? (
          <a
            key={attachment.id}
            href={attachment.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            title={attachment.fileName}
          >
            {label}
          </a>
        ) : (
          <div key={attachment.id} className={className} title={attachment.fileName}>
            {label}
          </div>
        )
      })}
    </div>
  )
}

interface Props {
  message: Message
  defaultExpanded: boolean
}

export function EmailThreadMessage({ message, defaultExpanded }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const isOutbound = message.direction === EmailDirection.OUTBOUND
  // The +alias that decided routing lives on the header, never on the mailbox.
  const addressedTo = message.toHeader || message.toEmail
  const viaMailbox = message.toHeader ? !message.toHeader.includes(message.toEmail) : false

  return (
    <article className="bg-card rounded-lg border">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="hover:bg-muted/40 flex w-full cursor-pointer gap-3 px-4 py-3 text-left max-lg:flex-col max-lg:gap-1 lg:items-start"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{message.fromEmail}</span>
            <Badge variant={isOutbound ? 'outline' : 'secondary'} className="text-xs">
              {isOutbound ? 'Sent' : 'Received'}
            </Badge>
            {message.bouncedAt && (
              <Badge variant="destructive" className="text-xs">
                Bounced
              </Badge>
            )}
            {message.isQa && <QaOutcomeBadge outcome={message.qaOutcome} />}
            {message.isQa && isOutbound && (
              <Badge variant="outline" className="text-xs">
                Captured — would have gone to {message.toEmail}
              </Badge>
            )}
            {message.attachments.length > 0 && (
              <Paperclip className="text-muted-foreground size-3.5" aria-label="Has attachments" />
            )}
          </div>
          {expanded ? (
            <div className="text-muted-foreground mt-1 flex flex-col gap-0.5 text-xs">
              <div>
                <span className="font-medium">To:</span> {addressedTo}
              </div>
              {viaMailbox && (
                <div>
                  <span className="font-medium">Mailbox:</span> {message.toEmail}
                </div>
              )}
              {message.ccEmail && (
                <div>
                  <span className="font-medium">Cc:</span> {message.ccEmail}
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted-foreground mt-0.5 truncate text-xs">To {addressedTo}</div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 max-lg:justify-between">
          <span className="text-muted-foreground text-xs">{formatDateTime(message.dateCreated)}</span>
          {expanded ? (
            <ChevronUp className="text-muted-foreground size-4" />
          ) : (
            <ChevronDown className="text-muted-foreground size-4" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 py-3">
          {message.bouncedAt && message.bounceReason && (
            <p className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {message.bounceReason}
            </p>
          )}
          <EmailMessageBody
            id={message.id}
            subject={message.subject}
            bodyHtml={message.bodyHtml}
            bodyText={message.bodyText}
          />
          <Attachments message={message} />
          <div className="text-muted-foreground mt-4 flex items-center gap-3 text-xs">
            <span>{message.provider}</span>
            {message.openCount > 0 && <span>Opened {message.openCount}×</span>}
            <a
              href={adminHref(`${ADMIN_BASE_URL}/insure/emailmessage/${message.id}/change/`)}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground inline-flex items-center gap-1"
            >
              Admin <ExternalLink className="size-3" />
            </a>
            {message.agentRunId && (
              <a
                href={adminHref(`${ADMIN_BASE_URL}/insure/agentrun/${message.agentRunId}/change/`)}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground inline-flex items-center gap-1"
              >
                Agent run <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </article>
  )
}
